/**
 * AsnOnePagerTable
 * High-performance virtualized table for ASN Location Details.
 *
 * Adapted from Analytics Hub's OnePagerTable with SPARC category functionality removed:
 * - No category header row
 * - No category style lookup functions
 * - No sparcBoundaryIndices calculations
 *
 * Architecture:
 * - useScrollSync hook for scroll synchronization
 * - OnePagerRow (memoized) for KPI data cells
 * - StickyRow (memoized) for grain dimension cells
 * - formatKpiValue from shared formatters
 *
 * • Receives all state through props
 * • Exposes callbacks only
 * • No Redux inside the component
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import {
  PagerColumn,
  TableSortState,
} from '../../../../AnalyticsHub/constants/onePagerConstants';
import { AsnLocationGrainKey } from '../api/asnOverviewKpiApi';
import { getAsnGrainLabel } from '../constants/asnOnePagerConstants';
import { AsnDisplayRow } from '../utils/asnLocationDetailsAdapter';
import {
  onePagerTableStyles,
  ROW_HEIGHT,
  MAX_VISIBLE_ROWS,
} from '../../../../AnalyticsHub/styles/onePagerTableStyles';
import { asnOnePagerTableStyles } from '../styles/asnOnePagerTableStyles';
import {
  formatKpiValue,
  formatTargetValue,
} from '../../../../AnalyticsHub/utils/onePagerFormatters';
import { useScrollSync } from '../../../../AnalyticsHub/hooks/useScrollSync';
import type { NavigableFilterType } from '../../../../AnalyticsHub/types/onePagerTypes';
import OnePagerRow from '../../../../AnalyticsHub/components/kpiOnePager/OnePagerRow';
import StickyRow from '../../../../AnalyticsHub/components/kpiOnePager/StickyRow';
import KpiTargetTooltip from '../../../../AnalyticsHub/components/KpiTargetTooltip';
import { calculateTableWidths } from '../utils/asnTableWidthUtils';
import TableSkeleton from '../../../components/TableSkeleton';

const styles = {
  ...onePagerTableStyles,
  ...asnOnePagerTableStyles,
  sectionHeaderActions: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 8,
    flexWrap: 'wrap' as const,
  },
  headerSearchWrap: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 6,
    height: 32,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
  },
  headerSearchInput: {
    width: 200,
    fontSize: 13,
    color: '#1A1D21',
  },
};

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AsnOnePagerTableProps {
  readonly rows: AsnDisplayRow[];
  readonly grandTotalRow: AsnDisplayRow | null;
  readonly grainSelections: AsnLocationGrainKey[];
  readonly columns: PagerColumn[];
  readonly sort: TableSortState;
  readonly loading: boolean;
  /** Backend-reported error — rendered in the empty state instead of the
   * generic "no data" copy when present. */
  readonly error?: string | null;
  readonly onSort: (col: string) => void;
  readonly onNavigateToOverview: (
    filterType: NavigableFilterType,
    value: string
  ) => void;
  readonly onExport?: () => void;
  readonly isExporting?: boolean;
  /** Search value rendered in the section-header search box. */
  readonly searchValue?: string;
  readonly onSearchChange?: (text: string) => void;
  /** Ref attached to the Columns button so the caller can measure it for dropdown positioning. */
  readonly columnsButtonRef?: React.RefObject<any>;
  readonly onColumnsPress?: () => void;
  // Pagination props (optional — table remains presentational)
  readonly totalRows?: number;
  readonly totalPages?: number;
  readonly page?: number;
  readonly pageSize?: number;
  readonly onPageChange?: (page: number) => void;
  readonly onPageSizeChange?: (size: number) => void;
  /** Current perspective for the table heading — "shipping" or "receiving". */
  readonly perspective?: 'shipping' | 'receiving';
}

// ─── Component ────────────────────────────────────────────────────────────────

const AsnOnePagerTable: React.FC<AsnOnePagerTableProps> = ({
  rows,
  grandTotalRow,
  grainSelections,
  columns,
  sort,
  loading,
  error,
  onSort,
  onNavigateToOverview,
  onExport,
  isExporting = false,
  searchValue,
  onSearchChange,
  columnsButtonRef,
  onColumnsPress,
  totalRows,
  totalPages,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  perspective,
}) => {
  const { refs, handlers } = useScrollSync();

  // ─── Table Width Tracking ──────────────────────────────────────────────
  const [tableWidth, setTableWidth] = useState(0);

  // ─── Derived calculations (memoized) ────────────────────────────────
  // Calculate dynamic column widths based on measured table width
  const widths = useMemo(
    () =>
      calculateTableWidths(tableWidth, grainSelections.length, columns.length),
    [tableWidth, grainSelections.length, columns.length]
  );

  // Use calculated grain and metric widths
  const stickyWidth = widths.grainWidth;
  const contentWidthStyle = useMemo(
    () => ({ width: widths.totalMetricWidth }),
    [widths.totalMetricWidth]
  );

  // Dynamic body height: shrink to fit when rows <= MAX_VISIBLE_ROWS, cap otherwise
  const bodyContainerStyle = useMemo(
    () => [
      styles.bodyContainer,
      { height: Math.min(rows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT },
    ],
    [rows.length]
  );

  // ─── FlatList optimizations ─────────────────────────────────────────
  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: ROW_HEIGHT,
      offset: ROW_HEIGHT * index,
      index,
    }),
    []
  );

  // The API guarantees a stable, unique `key` per row — use it directly
  // instead of deriving a composite key from grain values.
  const keyExtractor = useCallback((item: AsnDisplayRow) => item.key, []);

  // ─── Render callbacks (stable references) ───────────────────────────
  const renderStickyItem = useCallback(
    ({ item, index }: { item: AsnDisplayRow; index: number }) => (
      <StickyRow
        row={item}
        index={index}
        // StickyRow's prop type is fixed to the Analytics Hub GrainKey
        // domain ('rg' | 'mk' | ...), but it only ever does a plain
        // `row.grainVals[grainKey]` string lookup — safe to pass the
        // ASN-native grain keys through as-is since they match the keys
        // actually present on `row.grainVals` (populated from the API
        // response's `effectiveGrainKeys`/`grainVals`).
        grainSelections={grainSelections as unknown as any}
        cellWidth={widths.grainWidth / grainSelections.length}
        onNavigateToOverview={onNavigateToOverview}
      />
    ),
    [grainSelections, onNavigateToOverview, widths.grainWidth]
  );

  const renderBodyItem = useCallback(
    ({ item, index }: { item: AsnDisplayRow; index: number }) => (
      <OnePagerRow
        row={item}
        index={index}
        columns={columns}
        cellWidth={widths.metricWidth}
        sparcBoundaryIndices={new Set()}
      />
    ),
    [columns, widths.metricWidth]
  );

  // ─── Configuration validation (must check before any data checks) ───
  // If user hasn't selected columns or grains, show configuration message
  const hasNoConfig = columns.length === 0 || grainSelections.length === 0;

  // ─── Loading state ──────────────────────────────────────────────────
  if (loading && rows.length === 0 && !hasNoConfig) {
    return (
      <View style={styles.container}>
        <TableSkeleton rows={Math.min(8, pageSize ?? 8)} />
      </View>
    );
  }

  // ─── Configuration Message (when no columns or grains selected) ─────
  // Show this BEFORE the empty data state so users know what to do
  if (hasNoConfig) {
    return (
      <View style={styles.container}>
        {/* ═══ SECTION HEADER ═══ */}
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Text style={styles.sectionTitle}>
              {perspective === 'receiving' ? 'Receiving' : 'Shipping'} Details
            </Text>
          </View>
          <View style={styles.sectionHeaderActions}>
            {onColumnsPress && (
              <View ref={columnsButtonRef}>
                <TouchableOpacity
                  style={styles.exportButton}
                  onPress={onColumnsPress}
                  activeOpacity={0.7}
                  accessibilityLabel="Columns"
                  testID="asn-gap-location-summary-columns"
                >
                  <MaterialIcons name="tune" size={16} color="#034895" />
                  <Text style={styles.exportButtonText}>Columns</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
        {/* ═══ TABLE BODY WITH MESSAGE ═══ */}
        <View style={[styles.bodyContainer, { minHeight: 200 }]}>
          <View style={styles.emptyContainer}>
            <MaterialIcons name="tune" size={36} color="#ADB5BD" />
            <Text style={styles.emptyText}>
              {columns.length === 0 && grainSelections.length === 0
                ? 'Select at least one grain column and one metric using the Columns button.'
                : grainSelections.length === 0
                ? 'Select at least one grain column using the Columns button.'
                : 'Select at least one metric using the Columns button.'}
            </Text>
          </View>
        </View>
      </View>
    );
  }

  // ─── Empty state (no rows AND no grand total to show) ───────────────
  // If the backend still returned a grandTotalRow despite an empty page
  // (e.g. an empty search result but the unfiltered total is still
  // meaningful), fall through to the full table shell below instead —
  // the header/grand total stay visible and only the row area shows the
  // empty message, so the response is never fully discarded.
  if (!loading && rows.length === 0 && !grandTotalRow) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyContainer}>
          <MaterialIcons name="table-chart" size={36} color="#ADB5BD" />
          <Text style={styles.emptyText}>
            {error || 'No data available for the current configuration.'}
          </Text>
        </View>
      </View>
    );
  }

  const isEmpty = rows.length === 0;

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <View style={styles.container}>
      {/* ═══ SECTION HEADER ═══ */}
      <View style={styles.sectionHeader}>
        <View style={styles.sectionHeaderLeft}>
          <Text style={styles.sectionTitle}>
            {perspective === 'receiving' ? 'Receiving' : 'Shipping'} Details
          </Text>
          <Text style={styles.sectionHint}>
            Region / Unit level. Expand rows to drill to Market and Location.
          </Text>
        </View>
        <View style={styles.sectionHeaderActions}>
          {onSearchChange != null && (
            <View style={styles.headerSearchWrap}>
              <MaterialIcons name="search" size={16} color="#838383" />
              <TextInput
                style={styles.headerSearchInput}
                value={searchValue ?? ''}
                onChangeText={onSearchChange}
                placeholder="Search region, market, location..."
                placeholderTextColor="#9AA1AB"
                testID="asn-gap-location-summary-search"
              />
            </View>
          )}
          {onColumnsPress && (
            <View ref={columnsButtonRef}>
              <TouchableOpacity
                style={styles.exportButton}
                onPress={onColumnsPress}
                activeOpacity={0.7}
                accessibilityLabel="Columns"
                testID="asn-gap-location-summary-columns"
              >
                <MaterialIcons name="tune" size={16} color="#034895" />
                <Text style={styles.exportButtonText}>Columns</Text>
              </TouchableOpacity>
            </View>
          )}
          {onExport && (
            <TouchableOpacity
              style={[
                styles.exportButton,
                isExporting && styles.exportButtonDisabled,
              ]}
              onPress={onExport}
              disabled={isExporting}
              activeOpacity={0.7}
              accessibilityLabel="Export table data"
              testID="asn-gap-location-summary-export"
            >
              {isExporting ? (
                <>
                  <ActivityIndicator
                    size="small"
                    color="#034895"
                    style={styles.exportButtonSpinner}
                  />
                  <Text style={styles.exportButtonText}>Exporting...</Text>
                </>
              ) : (
                <>
                  <MaterialIcons
                    name="file-download"
                    size={16}
                    color="#034895"
                  />
                  <Text style={styles.exportButtonText}>Export</Text>
                </>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>

      <View
        style={styles.tableWrapper}
        onLayout={(event) => {
          setTableWidth(event.nativeEvent.layout.width);
        }}
      >
        {/* ═══ STICKY LEFT COLUMN ═══ */}
        <View style={[styles.stickyColumn, { width: stickyWidth }]}>
          <View style={[styles.stickyHeaderRow, styles.stickyHeaderWrapper]}>
            {grainSelections.map((grainKey) => {
              // Each grain header sorts by its own dimension. Previously every
              // header dispatched the shared 'geo' literal, so all of them
              // triggered the same action and lit up simultaneously.
              const isActiveSort = sort.col === grainKey;
              return (
                <TouchableOpacity
                  key={grainKey}
                  style={styles.stickyHeaderCell}
                  onPress={() => onSort(grainKey)}
                  activeOpacity={0.7}
                  accessibilityLabel={`Sort by ${getAsnGrainLabel(grainKey)}`}
                >
                  <Text style={styles.stickyHeaderText}>
                    {getAsnGrainLabel(grainKey)}
                  </Text>
                  <MaterialIcons
                    name={
                      isActiveSort
                        ? sort.dir === 'asc'
                          ? 'keyboard-arrow-up'
                          : 'keyboard-arrow-down'
                        : 'unfold-more'
                    }
                    size={14}
                    color={isActiveSort ? '#005CBC' : '#9E9E9E'}
                    style={styles.sortIcon}
                  />
                </TouchableOpacity>
              );
            })}
          </View>

          {grandTotalRow && (
            <View style={styles.grandTotalStickyRow}>
              {grainSelections.map((grainKey, idx) => (
                <View key={grainKey} style={styles.grandTotalCell}>
                  <Text style={styles.grandTotalCellText}>
                    {idx === 0 ? 'Grand Total' : ''}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {isEmpty ? (
            <View style={styles.emptyContainer}>
              <MaterialIcons name="table-chart" size={28} color="#ADB5BD" />
            </View>
          ) : (
            <View style={bodyContainerStyle}>
              <FlatList
                ref={refs.stickyListRef}
                data={rows}
                renderItem={renderStickyItem}
                keyExtractor={keyExtractor}
                getItemLayout={getItemLayout}
                onScroll={handlers.onStickyVerticalScroll}
                scrollEventThrottle={16}
                showsVerticalScrollIndicator={false}
                removeClippedSubviews
                maxToRenderPerBatch={20}
                windowSize={5}
                initialNumToRender={15}
              />
            </View>
          )}
        </View>

        {/* ═══ SCROLLABLE KPI AREA ═══ */}
        <View style={styles.scrollableArea}>
          {/* Header (sticky on vertical scroll) */}
          <View style={styles.stickyHeaderWrapper}>
            <ScrollView
              ref={refs.headerScrollRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              scrollEventThrottle={16}
              onScroll={handlers.onHeaderHorizontalScroll}
              bounces={false}
              contentContainerStyle={contentWidthStyle}
            >
              <View>
                <View style={styles.columnHeaderRow}>
                  {columns.map((col, colIdx) => {
                    const isActiveSort = sort.col === col.id;

                    return (
                      <TouchableOpacity
                        key={col.id}
                        style={[
                          styles.columnHeaderCell,
                          { width: widths.metricWidth },
                        ]}
                        onPress={() => onSort(col.id)}
                        activeOpacity={0.7}
                        accessibilityLabel={`Sort by ${col.label}`}
                      >
                        <View style={styles.columnHeaderTextWrap}>
                          <Text
                            style={styles.columnHeaderText}
                            numberOfLines={2}
                          >
                            {col.label}
                          </Text>
                        </View>
                        <MaterialIcons
                          name={
                            isActiveSort
                              ? sort.dir === 'asc'
                                ? 'keyboard-arrow-up'
                                : 'keyboard-arrow-down'
                              : 'unfold-more'
                          }
                          size={14}
                          color={isActiveSort ? '#005CBC' : '#9E9E9E'}
                          style={styles.sortIcon}
                        />
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            </ScrollView>
          </View>

          {/* Grand Total (below header, above data rows) */}
          {grandTotalRow && (
            <ScrollView
              ref={refs.grandTotalScrollRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              scrollEnabled={false}
              contentContainerStyle={contentWidthStyle}
            >
              <View style={styles.grandTotalRow}>
                {columns.map((col, colIdx) => {
                  const targetVal = grandTotalRow?.targets?.[col.id];
                  const kpiValue = grandTotalRow?.vals?.[col.id];
                  return (
                    <View
                      key={col.id}
                      style={[
                        styles.grandTotalKpiCell,
                        { width: widths.metricWidth },
                      ]}
                    >
                      <KpiTargetTooltip
                        metricName={col.label}
                        actualValue={formatKpiValue(kpiValue ?? 0, col)}
                        targetValue={
                          targetVal !== undefined
                            ? formatTargetValue(targetVal, col)
                            : null
                        }
                      >
                        <Text
                          style={styles.grandTotalKpiText}
                          numberOfLines={1}
                        >
                          {formatKpiValue(kpiValue ?? 0, col)}
                        </Text>
                      </KpiTargetTooltip>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          )}

          {/* Body */}
          {isEmpty ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                {error || 'No data available for the current configuration.'}
              </Text>
            </View>
          ) : (
            <View style={bodyContainerStyle}>
              <ScrollView
                ref={refs.bodyScrollRef}
                horizontal
                showsHorizontalScrollIndicator
                scrollEventThrottle={16}
                onScroll={handlers.onBodyHorizontalScroll}
                bounces={false}
                contentContainerStyle={contentWidthStyle}
              >
                <FlatList
                  ref={refs.bodyListRef}
                  data={rows}
                  renderItem={renderBodyItem}
                  keyExtractor={keyExtractor}
                  getItemLayout={getItemLayout}
                  onScroll={handlers.onBodyVerticalScroll}
                  scrollEventThrottle={16}
                  showsVerticalScrollIndicator
                  removeClippedSubviews
                  maxToRenderPerBatch={20}
                  windowSize={5}
                  initialNumToRender={15}
                />
              </ScrollView>
            </View>
          )}
        </View>
      </View>

      {/* ═══ PAGINATION BAR ═══ */}
      {totalRows != null &&
        page != null &&
        pageSize != null &&
        onPageChange && (
          <View style={styles.paginationBar}>
            <Text style={styles.paginationInfo}>
              {`Showing ${Math.min(
                (page - 1) * pageSize + 1,
                totalRows
              )}–${Math.min(page * pageSize, totalRows)} of ${totalRows}`}
            </Text>
            <View style={styles.paginationControls}>
              <TouchableOpacity
                style={[
                  styles.paginationButton,
                  page <= 1 && styles.paginationButtonDisabled,
                ]}
                onPress={() => onPageChange(page - 1)}
                disabled={page <= 1}
                activeOpacity={0.7}
                accessibilityLabel="Previous page"
              >
                <MaterialIcons
                  name="chevron-left"
                  size={20}
                  color={page <= 1 ? '#CED4DA' : '#005CBC'}
                />
              </TouchableOpacity>
              <Text style={styles.paginationPageText}>{`Page ${page} of ${
                totalPages ?? 1
              }`}</Text>
              <TouchableOpacity
                style={[
                  styles.paginationButton,
                  page * pageSize >= totalRows &&
                    styles.paginationButtonDisabled,
                ]}
                onPress={() => onPageChange(page + 1)}
                disabled={page * pageSize >= totalRows}
                activeOpacity={0.7}
                accessibilityLabel="Next page"
              >
                <MaterialIcons
                  name="chevron-right"
                  size={20}
                  color={page * pageSize >= totalRows ? '#CED4DA' : '#005CBC'}
                />
              </TouchableOpacity>
            </View>
            {onPageSizeChange && (
              <View style={styles.paginationSizeSelector}>
                {[25, 50, 100].map((size) => (
                  <TouchableOpacity
                    key={size}
                    style={[
                      styles.paginationSizeOption,
                      pageSize === size && styles.paginationSizeOptionActive,
                    ]}
                    onPress={() => onPageSizeChange(size)}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[
                        styles.paginationSizeText,
                        pageSize === size && styles.paginationSizeTextActive,
                      ]}
                    >
                      {size}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        )}
    </View>
  );
};

AsnOnePagerTable.displayName = 'AsnOnePagerTable';

export default React.memo(AsnOnePagerTable);
